/** AI summary display block — clamp-2, expand toggle, state variants */

import { useState } from 'react'
import { Loader2, Sparkles } from 'lucide-react'
import { cn } from '../../lib/utils'
import { getWebFeatureProfile } from '../../lib/feature-flags'

interface SummaryBlockProps {
  summary: string | null
  status: string | null
  extractionMethod: string | null
  /** MIME type — images always render the summary block regardless of extractionMethod. */
  contentType?: string | null
  isDark: boolean
  /** Called when user clicks "Generate" (summary is null + status completed) */
  onGenerate?: () => void
  /** True while regenerate mutation is in-flight */
  generatePending?: boolean
}

export function SummaryBlock({
  summary,
  status,
  extractionMethod,
  contentType,
  isDark,
  onGenerate,
  generatePending,
}: SummaryBlockProps) {
  const [expanded, setExpanded] = useState(false)
  const extractionEnabled = getWebFeatureProfile().extraction

  if (!extractionEnabled) return null

  const mutedText = isDark ? 'text-neutral-500' : 'text-neutral-400'
  const bodyText = isDark ? 'text-neutral-300' : 'text-neutral-600'
  const isImage = contentType?.startsWith('image/') ?? false

  // Hide for unsupported file types — except images, which use the vision pipeline
  // and may carry a historical extractionMethod='unsupported' from before the
  // auto-vision-summarize flow shipped.
  if (extractionMethod === 'unsupported' && !isImage) return null

  // Processing skeleton
  if (status === 'processing') {
    return (
      <div className="min-h-[2.5rem] px-2 pb-2">
        <div className="flex items-center gap-1.5">
          <Loader2 className={cn('h-3 w-3 animate-spin', mutedText)} />
          <span className={cn('text-[10px]', mutedText)}>Generating summary…</span>
        </div>
      </div>
    )
  }

  // No summary yet but extraction completed — offer Generate
  if (!summary && status === 'completed') {
    return (
      <div className="min-h-[2.5rem] px-2 pb-2">
        <button
          onClick={onGenerate}
          disabled={generatePending}
          className={cn(
            'flex items-center gap-1 text-[10px] transition-colors',
            generatePending ? mutedText : isDark ? 'text-brand-400 hover:text-brand-300' : 'text-brand-600 hover:text-brand-500',
          )}
          aria-label="Generate AI summary"
        >
          {generatePending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Sparkles className="h-3 w-3" />
          )}
          {generatePending ? 'Generating…' : 'Generate summary'}
        </button>
      </div>
    )
  }

  // No summary (pending/failed/null status)
  if (!summary) {
    return <div className="min-h-[2.5rem]" aria-hidden="true" />
  }

  // Loaded summary
  return (
    <div className="min-h-[2.5rem] px-2 pb-2">
      <p
        className={cn('text-[10px] leading-relaxed', bodyText, !expanded && 'line-clamp-2')}
      >
        {summary}
      </p>
      {summary.length > 120 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className={cn('mt-0.5 text-[10px]', isDark ? 'text-brand-400 hover:text-brand-300' : 'text-brand-600 hover:text-brand-500')}
          aria-label={expanded ? 'Collapse summary' : 'Expand summary'}
        >
          {expanded ? 'Collapse' : 'Expand'}
        </button>
      )}
    </div>
  )
}
